'use client';

import React, { useState, useEffect, useCallback, useRef, Component, ErrorInfo, ReactNode } from 'react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import { getUserBookmarks, getUserResonanceSummary, BookmarkItem, ResonanceSummary } from '@/lib/bookmarks';
import { createClient } from '@/lib/supabase/client';

import { FirstVisitReveal, FitrahSeed, CommunityGardenContext, TafakkurMode, TafakkurToggle, NiyyahField, OpenLetterQuoteBar, FajrDiscoveryToast, AtmosphericBackground, getSoilPurityState, getPrayerState, LineageEmergence, RootConnectionToast, MaghribAyahOverlay, ThreeStageProgressBar, DhikrNotification, type PrayerState, getGardenStage, getPromptDensity, HarvestModal, LongleafPineNote, PrayerOrientationTooltip, type HarvestCycleSummary, type GardenStage,  } from './components/GardenOntology';
import { FounderVoice, GardenExitOverlay, useFounderVoice, type FounderVoiceKey } from './components/FounderVoice';
import { NiyyahField as NiyyahModal, MuhasabaMoment, type NiyyahRecord } from './components/NiyyahEngine';
import { MizanIndicator, computeMizanState, type MizanData } from './components/MizanSystem';
import { SunnahModule } from './components/SunnahModule';
import { DualVoiceDisplay, GardenLoadingVoice, BarzakhTransition, FikrJournal } from './components/PlotTwoEnhancements';

import { RecursiveRevisitationSystem } from '../open-letter/components/Plot3MuralEnhancements';
import {
  YaqeenWeightedCounter,
  QuranEncounterModule,
  InnerHawasModule,
  TripartiteAyatSystem,
  ImperfectionModel,
  CommunityRoleModuleCreator,
  AynYaqeenTensionNote,
} from './components/Plot3GardenEnhancements';
import RoleGardenDashboard, { RoleLineageBadge } from './components/RoleGardenDashboard';
import LineageDashboards from './components/LineageDashboards';
import {
  AlFatihaSesssionOpening,
  AlhamdulillahFoundationalCounter,
  YaqeenModuleTile,
  RemindIfBenefitsCheck,
  SubsurfaceLayersVisualization,
} from './components/Plot4GardenEnhancements';
import {
  RoleSelectorWithLineage,
  ModuleTileWithRolePrompt,
  VocationLensModule,
  VocationalConnectionsMap,
  RootModulesArchitecture,
  IRLLensSelfReferential,
} from '../open-letter/components/Plot5Enhancements';

import KitbashGardenCanvas from './components/KitbashGardenCanvas';
import ClaudeBatchProcessor from './components/ClaudeBatchProcessor';
import { ActingStageFooter } from '@/app/user-dashboard/components/GardenOntology';




// ─── TYPES ────────────────────────────────────────────────────────────────────

type DashboardTab = 'garden' | 'the-mural' | 'history' | 'saved' | 'quests' | 'garment' | 'badges' | 'roadmap';

interface ModuleNode {
  id: string;
  label: string;
  href: string;
  x: number;
  y: number;
  color: string;
  visitCount: number;
  resonanceDepth: number;
}

interface HistoryItem {
  id: string;
  content_type: string;
  content_id: string;
  content_title: string;
  resonance_type: string;
  created_at: string;
}

interface Quest {
  id: string;
  title: string;
  description: string;
  status: string;
  caretaker_role: string;
  created_at: string;
}

// ─── WIDGET SYSTEM ────────────────────────────────────────────────────────────

interface Widget {
  id: string;
  type: string;
  label: string;
  icon: string;
  color: string;
  description: string;
  size: 'sm' | 'md' | 'lg';
  href?: string;
  component?: string;
}

const AVAILABLE_WIDGETS: Widget[] = [
  { id: 'observation-chain', type: 'observation-chain', label: 'Observation Chain', icon: '◎', color: '#2d8a87', description: 'Your most recent chain — add observations in one tap', size: 'lg', component: 'observation-chain' },
  { id: 'ai-quests', type: 'ai-quests', label: 'Personalized Quests', icon: '✦', color: '#c9a84c', description: 'Recognition-pattern quests generated for your journey', size: 'lg', component: 'ai-quests' },
  { id: 'irl-lens', type: 'irl-lens', label: 'Inner Reality Lens', icon: '◎', color: '#2d8a87', description: 'Your personal IRL tool — simplified for your context', size: 'md', href: '/inner-reality-lens', component: 'irl' },
  { id: 'mrg', type: 'mrg', label: 'Muslim Resource Guide', icon: '⬡', color: '#2d7a47', description: 'Curated resources tailored to your interests and city', size: 'md', href: '/muslim-resource-guide', component: 'mrg' },
  { id: 'framework-map', type: 'framework-map', label: 'The Mural', icon: '◈', color: '#c9a84c', description: 'The central hub — a contemplative overview of all regions', size: 'lg', component: 'map' },
  { id: 'quests', type: 'quests', label: 'Active Quests', icon: '⌘', color: '#D4A82A', description: 'Your current quests and progress', size: 'md', component: 'quests' },
  { id: 'badges', type: 'badges', label: 'Alhamdulilah Board', icon: '✦', color: '#8a8ab8', description: 'Your earned recognitions and milestones', size: 'md', component: 'badges' },
  { id: 'saved', type: 'saved', label: 'Saved Collection', icon: '◈', color: '#c9a84c', description: 'Bookmarked verses, names, and concepts', size: 'md', component: 'saved' },
  { id: 'history', type: 'history', label: 'Resonance History', icon: '◎', color: '#2d8a87', description: 'Your interaction trail across the framework', size: 'md', component: 'history' },
  { id: 'garment', type: 'garment', label: 'My Garment', icon: '⬡', color: '#5a5a8a', description: 'Your woven garment visualization', size: 'lg', component: 'garment' },
  { id: 'notes', type: 'notes', label: 'Caretaker Notes', icon: '✦', color: '#2d7a47', description: 'Personal notes and reflections', size: 'sm', component: 'notes' },
  { id: 'keys', type: 'keys', label: 'Keys to Kulli Haal', icon: '⌘', color: '#D4A82A', description: 'Quick access to the Keys framework', size: 'sm', href: '/keys-to-kulli-haal' },
  { id: 'ayat', type: 'ayat', label: 'Ayat in Creation', icon: '☽', color: '#2d7a47', description: 'Quranic signs in the world around you', size: 'sm', href: '/ayat-in-creation' },
  { id: 'names', type: 'names', label: 'Names Reference', icon: '✦', color: '#8a8ab8', description: 'The 99 Names and their resonances', size: 'sm', href: '/names-reference' },
];

// ─── BADGE DEFINITIONS ────────────────────────────────────────────────────────

interface Badge {
  id: string;
  label: string;
  desc: string;
  icon: string;
  color: string;
  category: 'framework' | 'community' | 'quest-chain' | 'milestone';
  condition: (stats: BadgeStats) => boolean;
}

interface BadgeStats {
  totalInteractions: number;
  bookmarkCount: number;
  verseClicks: number;
  moduleVisitCount: number;
  uniqueModules: number;
  topNames: string[];
  topKeys: string[];
  questsCompleted: number;
  streakDays: number;
}

const BADGES: Badge[] = [
  { id: 'first-step', label: 'First Step', desc: 'Made your first recognition', icon: '◎', color: '#2d7a47', category: 'milestone', condition: s => s.totalInteractions >= 1 },
  { id: 'keys-explorer', label: 'Keys Explorer', desc: 'Explored the Keys framework', icon: '⌘', color: '#D4A82A', category: 'framework', condition: s => s.topKeys.length >= 1 },
  { id: 'names-seeker', label: 'Names Seeker', desc: 'Bookmarked 3 Names of Allah', icon: '✦', color: '#8a8ab8', category: 'framework', condition: s => s.topNames.length >= 3 },
  { id: 'verse-witness', label: 'Verse Witness', desc: 'Clicked 5 Ayat in Creation', icon: '☽', color: '#2d7a47', category: 'framework', condition: s => s.verseClicks >= 5 },
  { id: 'deep-reader', label: 'Deep Reader', desc: 'Reached 25 total recognitions', icon: '◈', color: '#c9a84c', category: 'milestone', condition: s => s.totalInteractions >= 25 },
  { id: 'framework-walker', label: 'Framework Walker', desc: 'Visited 4 or more modules', icon: '⬡', color: '#2d8a87', category: 'framework', condition: s => s.uniqueModules >= 4 },
  { id: 'collection-keeper', label: 'Collection Keeper', desc: 'Saved 10 items to your collection', icon: '◈', color: '#c9a84c', category: 'milestone', condition: s => s.bookmarkCount >= 10 },
  { id: 'scholar-of-haal', label: 'Scholar of Haal', desc: 'Reached 50 total recognitions', icon: '⌘', color: '#8a8ab8', category: 'milestone', condition: s => s.totalInteractions >= 50 },
  { id: 'quest-beginner', label: 'Quest Beginner', desc: 'Completed your first quest', icon: '◎', color: '#2d7a47', category: 'quest-chain', condition: s => s.questsCompleted >= 1 },
  { id: 'quest-chain-3', label: 'Quest Chain', desc: 'Completed 3 quests', icon: '✦', color: '#D4A82A', category: 'quest-chain', condition: s => s.questsCompleted >= 3 },
  { id: 'quest-master', label: 'Quest Master', desc: 'Completed 7 quests', icon: '⌘', color: '#8a8ab8', category: 'quest-chain', condition: s => s.questsCompleted >= 7 },
  { id: 'community-member', label: 'Community Member', desc: 'Visited the Community page', icon: '⬡', color: '#2d7a47', category: 'community', condition: s => s.uniqueModules >= 2 },
  { id: 'streak-3', label: '3-Day Streak', desc: 'Returned for 3 consecutive days', icon: '◎', color: '#D4A82A', category: 'milestone', condition: s => s.streakDays >= 3 },
  { id: 'streak-7', label: '7-Day Streak', desc: 'Returned for 7 consecutive days', icon: '✦', color: '#c9a84c', category: 'milestone', condition: s => s.streakDays >= 7 },
];

// ─── ALHAMDULILAH SYSTEM ──────────────────────────────────────────────────────

interface AlhamdulilahLevel {
  level: number;
  label: string;
  arabic: string;
  minCount: number;
  maxCount: number;
  color: string;
  meaning: string;
}

const ALHAMDULILAH_LEVELS: AlhamdulilahLevel[] = [
  { level: 1, label: 'Mata', arabic: 'مَاطَا', minCount: 0, maxCount: 10, color: '#2d7a47', meaning: 'The first encounter — seeing with fresh eyes' },
  { level: 2, label: 'Encounter', arabic: 'لِقَاء', minCount: 10, maxCount: 25, color: '#2d8a87', meaning: 'Meeting the signs in your surroundings' },
  { level: 3, label: 'Examination', arabic: 'فَحْص', minCount: 25, maxCount: 50, color: '#D4A82A', meaning: 'Sitting with what you have found' },
  { level: 4, label: 'Recognition', arabic: 'مَعْرِفَة', minCount: 50, maxCount: 100, color: '#c9a84c', meaning: 'Knowing through the heart, not just the mind' },
  { level: 5, label: 'Re-Assembly', arabic: 'إِعَادَة', minCount: 100, maxCount: 200, color: '#8a8ab8', meaning: 'Weaving what you know into how you live' },
  { level: 6, label: 'Uboor', arabic: 'عُبُور', minCount: 200, maxCount: Infinity, color: '#D4A82A', meaning: 'Crossing — the journey becomes the path itself' },
];

function computeAlhamdulilahCount(stats: BadgeStats): number {
  return (
    stats.totalInteractions * 1 +
    stats.bookmarkCount * 2 +
    stats.verseClicks * 3 +
    stats.uniqueModules * 5 +
    stats.questsCompleted * 10 +
    stats.streakDays * 4
  );
}

function getAlhamdulilahLevel(count: number): AlhamdulilahLevel {
  return ALHAMDULILAH_LEVELS.slice().reverse().find(l => count >= l.minCount) || ALHAMDULILAH_LEVELS[0];
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  glossary_term: '#c9a84c', framework_page: '#2d8a87', verse_link: '#2d7a47',
  names_entry: '#8a8ab8', key_concept: '#d4a82a', phase_page: '#5a5a8a',
  module_visit: '#2d8a87', glossary_search: '#c9a84c', term_expand: '#c9a84c',
  role_select: '#d4a82a', view: 'rgba(212,197,169,0.4)',
};

const TYPE_ICONS: Record<string, string> = {
  bookmark: '◈', verse_click: '☽', module_visit: '⬡', view: '◎',
  deep_read: '✦', search: '🔍', term_expand: '◈', role_select: '⌘', glossary_search: '🔍',
};

const RECOGNITION_STAGES = [
  { id: 'mata', label: 'Mata', desc: 'Encounter' },
  { id: 'encounter', label: 'Encounter', desc: 'Examination' },
  { id: 'examination', label: 'Examination', desc: 'Recognition' },
  { id: 'recognition', label: 'Recognition', desc: 'Re-Assembly' },
  { id: 'reassembly', label: 'Re-Assembly', desc: 'Uboor' },
  { id: 'uboor', label: 'Uboor', desc: 'Crossing' },
];

const MODULE_NODES: ModuleNode[] = [
  { id: 'keys', label: 'Keys', href: '/keys-to-kulli-haal', x: 120, y: 80, color: '#d4a82a', visitCount: 0, resonanceDepth: 0 },
  { id: 'ayat', label: 'Ayat', href: '/ayat-in-creation', x: 280, y: 60, color: '#2d7a47', visitCount: 0, resonanceDepth: 0 },
  { id: 'irl', label: 'IRL Lens', href: '/inner-reality-lens', x: 420, y: 100, color: '#2d8a87', visitCount: 0, resonanceDepth: 0 },
  { id: 'names', label: 'Names', href: '/names-reference', x: 200, y: 180, color: '#8a8ab8', visitCount: 0, resonanceDepth: 0 },
  { id: 'mrg', label: 'MRG', href: '/muslim-resource-guide', x: 360, y: 200, color: '#2d7a47', visitCount: 0, resonanceDepth: 0 },
  { id: 'letter', label: 'Open Letter', href: '/open-letter', x: 100, y: 220, color: '#c9a84c', visitCount: 0, resonanceDepth: 0 },
  { id: 'phases', label: 'Phases', href: '/phase', x: 480, y: 180, color: '#5a5a8a', visitCount: 0, resonanceDepth: 0 },
  { id: 'glossary', label: 'SL Glossary', href: '/sl-glossary', x: 300, y: 280, color: '#c9a84c', visitCount: 0, resonanceDepth: 0 },
];

// ─── FRAMEWORK MAP STAGES ─────────────────────────────────────────────────────

interface FrameworkStage {
  id: string;
  label: string;
  arabic: string;
  description: string;
  modules: string[];
  color: string;
  icon: string;
}

const FRAMEWORK_STAGES: FrameworkStage[] = [
  { id: 'stage-1', label: 'Orientation', arabic: 'التوجيه', description: 'First contact with the framework', modules: ['guided-intro', 'guided-walkthrough'], color: '#2d7a47', icon: '◎' },
  { id: 'stage-2', label: 'Keys', arabic: 'المفاتيح', description: 'The foundational keys that unlock recognition', modules: ['keys-to-kulli-haal'], color: '#D4A82A', icon: '⌘' },
  { id: 'stage-3', label: 'Signs', arabic: 'الآيات', description: 'Reading Ayat in creation', modules: ['ayat-in-creation'], color: '#2d7a47', icon: '☽' },
  { id: 'stage-4', label: 'Inner Reality', arabic: 'الحقيقة الداخلية', description: 'The IRL Lens', modules: ['inner-reality-lens', 'irl-lens'], color: '#2d8a87', icon: '◈' },
  { id: 'stage-5', label: 'Resources', arabic: 'الموارد', description: 'Muslim Resource Guide', modules: ['muslim-resource-guide'], color: '#2d7a47', icon: '⬡' },
  { id: 'stage-6', label: 'Names', arabic: 'الأسماء', description: 'The Names of Allah', modules: ['names-reference'], color: '#8a8ab8', icon: '✦' },
  { id: 'stage-7', label: 'Community', arabic: 'الجماعة', description: 'Sharing and growing with the community', modules: ['community'], color: '#c9a84c', icon: '⬡' },
];

// ─── SKELETON ─────────────────────────────────────────────────────────────────

function Skeleton({ width = '100%', height = 16, className = '' }: { width?: string | number; height?: number; className?: string }) {
  return <div className={`rounded animate-pulse ${className}`} style={{ width, height, background: 'rgba(212,197,169,0.06)' }} />;
}

// ─── FIKR JOURNAL MODULE ──────────────────────────────────────────────────────
function FikrJournalModule() {
  return (
    <div className="rounded-xl p-5" style={{ background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)' }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,138,135,0.6)' }}>◎ Fikr Journal</p>
      <p className="font-mono text-[8px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.3)' }}>مَجَلَّة الْفِكْر — Field Notes · No Prompts · No Frameworks</p>
      <FikrJournal />
    </div>
  );
}

// ─── HAAL STATE AMBIENT ───────────────────────────────────────────────────────

function getHaalStateBackground(level: AlhamdulilahLevel, levelProgress: number): string {
  const intensity = 0.03 + (levelProgress / 100) * 0.04;
  const colorMap: Record<number, string> = {
    1: `radial-gradient(ellipse at 50% 0%, rgba(45,122,71,${intensity}) 0%, transparent 60%)`,
    2: `radial-gradient(ellipse at 50% 0%, rgba(45,138,135,${intensity}) 0%, transparent 60%)`,
    3: `radial-gradient(ellipse at 50% 0%, rgba(212,168,42,${intensity}) 0%, transparent 60%)`,
    4: `radial-gradient(ellipse at 50% 0%, rgba(201,168,76,${intensity + 0.02}) 0%, transparent 60%)`,
    5: `radial-gradient(ellipse at 50% 0%, rgba(138,138,184,${intensity}) 0%, transparent 60%)`,
    6: `radial-gradient(ellipse at 50% 0%, rgba(212,168,42,${intensity + 0.03}) 0%, transparent 60%)`,
  };
  return colorMap[level.level] || colorMap[1];
}

// ─── ROLE-ADAPTIVE WIDGET DEFAULTS ───────────────────────────────────────────

function getRoleAdaptiveWidgets(role: string, moduleVisits: Record<string, number>): string[] {
  const roleDefaults: Record<string, string[]> = {
    'community-caretaker': ['observation-chain', 'framework-map', 'mrg', 'quests', 'badges', 'notes'],
    'inner-seeker': ['observation-chain', 'irl-lens', 'framework-map', 'badges', 'saved', 'notes'],
    'academic': ['framework-map', 'observation-chain', 'names', 'ayat', 'saved', 'quests'],
    'activist': ['observation-chain', 'mrg', 'framework-map', 'quests', 'badges', 'notes'],
    'explorer': ['framework-map', 'observation-chain', 'irl-lens', 'quests', 'badges', 'saved'],
    'local-resource': ['mrg', 'observation-chain', 'framework-map', 'quests', 'notes', 'saved'],
  };
  return roleDefaults[role] || roleDefaults['explorer'];
}

// ─── EXPORT JOURNEY ARCHIVE ───────────────────────────────────────────────────

function exportJourneyArchive(data: any) {
  const text = `THE HAAL PROJECT — JOURNEY ARCHIVE
Exported: ${new Date().toLocaleDateString()}
Role: ${data.roleLabel}
Alhamdulilah Count: ${data.alhamdulilahCount}
Level: ${data.currentLevel.label}
Badges Earned: ${data.earnedBadges.length}
Quests Completed: ${data.quests?.filter((q: any) => q.status === 'completed').length || 0}
Bookmarks: ${data.bookmarks?.length || 0}
History Items: ${data.history?.length || 0}

"We aren't some spontaneous occurrence. Reflect on the perfect systems and design of creation around us."
— Open Letter to Imprisoned Caretakers
`;
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `haal-journey-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── STATIC QUESTS ────────────────────────────────────────────────────────────

const STATIC_QUESTS = [
  {
    id: 'leaf-sequence',
    title: 'The Leaf Sequence',
    description: 'Apply the Three Questions to a leaf or plant you can observe right now.',
    type: 'irl-lens',
    difficulty: 'Beginner',
    names: ['Al-Musawwir', 'Al-Khaliq', 'Al-Muhyi'],
    ayat: '6:99',
    ayatText: 'It is He who sends down water from the sky; from it We bring forth the growth of everything.',
    amanah: 'The leaf is an Ayah. Catalog it.',
    color: '#2d7a47',
    href: '/inner-reality-lens',
  },
  {
    id: 'ward-amanah',
    title: 'Ward Amanah',
    description: 'Identify one resource gap in your ward and find a corresponding MRG resource.',
    type: 'mrg',
    difficulty: 'Intermediate',
    names: ['Ar-Razzaq', 'Al-Adl', 'Al-Hafiz'],
    ayat: '5:2',
    ayatText: 'Cooperate in righteousness and piety.',
    amanah: 'The neighborhood as a field of Ayat — reading your ward as a living text.',
    color: '#2d7a47',
    href: '/muslim-resource-guide',
  },
  {
    id: 'mycorrhizal-network',
    title: 'The Mycorrhizal Network',
    description: 'Map three connections between modules you have visited. How do they feed each other?',
    type: 'framework',
    difficulty: 'Intermediate',
    names: ['Al-Jami', 'Ar-Razzaq', 'Al-Wali'],
    ayat: '49:10',
    ayatText: 'The believers are but brothers.',
    amanah: 'The underground ummah — connections that nourish without being seen.',
    color: '#2d8a87',
    href: '/ayat-in-creation',
  },
  {
    id: 'uboor-bridge',
    title: 'Uboor Bridge',
    description: 'Complete a full IRL Lens observation and bridge it to an MRG resource.',
    type: 'bridge',
    difficulty: 'Advanced',
    names: ['Al-Haqq', 'Al-Basir', 'Ar-Razzaq'],
    ayat: '41:53',
    ayatText: 'We will show them Our signs in the horizons and within themselves.',
    amanah: 'Recognition becomes service. The bridge is the Amanah.',
    color: '#c9a84c',
    href: '/inner-reality-lens',
  },
  {
    id: 'three-questions',
    title: 'Three Questions',
    description: 'Apply Is it? / What is it? / When is it? to something in your immediate environment.',
    type: 'irl-lens',
    difficulty: 'Beginner',
    names: ['Al-Khaliq', 'Al-Musawwir', 'Al-Alim'],
    ayat: '87:1-3',
    ayatText: 'Glorify the name of your Lord, the Most High, Who created and proportioned.',
    amanah: 'The Three Questions are the lens. Apply them to what is nearest.',
    color: '#2d8a87',
    href: '/inner-reality-lens',
  },
  {
    id: 'fisabilillah-khidmah',
    title: 'Fisabilillah Khidmah',
    description: 'Perform one act of service in your ward and document it as an Amanah fulfilled.',
    type: 'community',
    difficulty: 'Advanced',
    names: ['Ar-Rahman', 'Al-Karim', 'Al-Wadud'],
    ayat: '2:177',
    ayatText: 'Righteousness is to give wealth despite love for it to relatives, orphans, the needy, the traveler.',
    amanah: 'Service is the Amanah made visible. Document it.',
    color: '#8a8ab8',
    href: '/community',
  },
];

// ─── CYCLE CEILING CONSTANT ───────────────────────────────────────────────────
const CYCLE_CEILING = 14;

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────

export default function UserDashboardPage() {
  const [activeTab, setActiveTab] = useState<DashboardTab>('garden');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [resonanceSummary, setResonanceSummary] = useState<ResonanceSummary | null>(null);
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [moduleVisits, setModuleVisits] = useState<Record<string, number>>({});
  const [role, setRole] = useState('explorer');
  const [questsCompleted, setQuestsCompleted] = useState(0);
  // Ontological state
  const [showFirstVisit, setShowFirstVisit] = useState(false);
  const [tafakkurActive, setTafakkurActive] = useState(false);
  const [prayerState, setPrayerState] = useState<PrayerState>('isha');
  const [soilPurity, setSoilPurity] = useState<'rich' | 'neutral' | 'dry'>('neutral');
  // Prop 2 state
  const [lineage, setLineage] = useState<string | null>(null);
  const [interactionLog, setInteractionLog] = useState<string[]>([]);
  const [rootConnection, setRootConnection] = useState<any>(null);
  const [gardenVisitCount, setGardenVisitCount] = useState(0);
  const [dhikrTrigger, setDhikrTrigger] = useState<'quest-reminder' | 'cohort-update' | 'return-prompt' | null>(null);
  const [dhikrMode, setDhikrMode] = useState(true);
  // Proposition 4 state
  const [harvestModalVisible, setHarvestModalVisible] = useState(false);
  const [harvestSummary, setHarvestSummary] = useState<HarvestCycleSummary>({
    topModules: [], topNames: [], cycleNumber: 1, totalCumulative: 0,
  });
  const [cycleCount, setCycleCount] = useState(0);
  const [cycleAlhamdulilah, setCycleAlhamdulilah] = useState(0);
  // Founder Voice, Niyyah, Mizan state
  const { activeVoice, fire: fireFounderVoice, dismiss: dismissFounderVoice } = useFounderVoice();
  const [niyyahModalOpen, setNiyyahModalOpen] = useState(false);
  const [niyyahContext, setNiyyahContext] = useState<{ actionType: NiyyahRecord['actionType']; actionId: string; actionLabel: string; onConfirm: (r: NiyyahRecord) => void } | null>(null);
  const [muhasabaOpen, setMuhasabaOpen] = useState(false);
  const [muhasabaRecord, setMuhasabaRecord] = useState<NiyyahRecord | null>(null);
  const [mizanData, setMizanData] = useState<MizanData>({ recognitionCount: 0, actionCount: 0, ratio: 0.5, state: 'balanced' });
  const [showSunnahModule, setShowSunnahModule] = useState(false);
  // Plot 2 state
  const [showBarzakhTransition, setShowBarzakhTransition] = useState(false);
  const [barzakhType, setBarzakhType] = useState<'depth_unlock' | 'biome_crossing' | 'recognition_milestone' | 'stage_transition'>('depth_unlock');
  const [barzakhFrom, setBarzakhFrom] = useState('');
  const [barzakhTo, setBarzakhTo] = useState('');
  const [vocationLens, setVocationLens] = useState('explorer');
  const [totalAlhamdulilah, setTotalAlhamdulilah] = useState(0);

  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab') as DashboardTab;
      if (tab) setActiveTab(tab);
      const savedRole = localStorage.getItem('haal_role') || 'explorer';
      setRole(savedRole);
      const savedVocation = localStorage.getItem('haal_vocation_lens') || savedRole;
      setVocationLens(savedVocation);
      const hasVisited = localStorage.getItem('haal_garden_first_visit');
      if (!hasVisited) setShowFirstVisit(true);
      setPrayerState(getPrayerState());
      setSoilPurity(getSoilPurityState());
      const savedLineage = localStorage.getItem('haal_lineage');
      if (savedLineage) setLineage(savedLineage);
      const visits = parseInt(localStorage.getItem('haal_garden_visits') || '0', 10) + 1;
      localStorage.setItem('haal_garden_visits', String(visits));
      setGardenVisitCount(visits);
      const dm = localStorage.getItem('haal_dhikr_mode');
      setDhikrMode(dm !== 'false');
      const savedCycleCount = parseInt(localStorage.getItem('haal_cycle_count') || '0', 10);
      const savedCycleAlhamdulilah = parseInt(localStorage.getItem('haal_cycle_alhamdulilah') || '0', 10);
      setCycleCount(savedCycleCount);
      setCycleAlhamdulilah(savedCycleAlhamdulilah);
      setTotalAlhamdulilah(savedCycleAlhamdulilah);
      const lastVisit = localStorage.getItem('haal_last_visit_date');
      const today = new Date().toDateString();
      if (lastVisit && lastVisit !== today) {
        const daysSince = (Date.now() - new Date(lastVisit).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince >= 3) setTimeout(() => setDhikrTrigger('return-prompt'), 3000);
      }
      localStorage.setItem('haal_last_visit_date', today);
      const interval = setInterval(() => {
        setPrayerState(getPrayerState());
        setSoilPurity(getSoilPurityState());
      }, 60000);
      return () => clearInterval(interval);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data: { user: authUser } } = await supabase.auth.getUser();
    setUser(authUser);
    const [summary, bmarks] = await Promise.all([getUserResonanceSummary(), getUserBookmarks()]);
    setResonanceSummary(summary);
    setBookmarks(bmarks);
    const sessionId = typeof window !== 'undefined' ? sessionStorage.getItem('haal_session') || '' : '';
    let historyQuery = supabase.from('content_resonance').select('*').order('created_at', { ascending: false }).limit(50);
    if (authUser) historyQuery = historyQuery.eq('user_id', authUser.id);
    else historyQuery = historyQuery.eq('session_id', sessionId);
    const { data: histData } = await historyQuery;
    setHistory(histData || []);
    let visitsQuery = supabase.from('module_visits').select('module_id, visit_count');
    if (authUser) visitsQuery = visitsQuery.eq('user_id', authUser.id);
    else visitsQuery = visitsQuery.eq('session_id', sessionId);
    const { data: visitsData } = await visitsQuery;
    const visitMap: Record<string, number> = {};
    (visitsData || []).forEach((v: any) => { visitMap[v.module_id] = v.visit_count; });
    setModuleVisits(visitMap);
    if (authUser) {
      const { data: questData } = await supabase.from('caretaker_quests').select('*').eq('user_role', role).limit(30);
      setQuests(questData || []);
      const completedCount = (questData || []).filter((q: any) => q.quest_status === 'completed').length;
      setQuestsCompleted(completedCount);
    }
    setLoading(false);
  }, [supabase, role]);

  useEffect(() => { loadData(); }, [loadData]);

  const badgeStats: BadgeStats = {
    totalInteractions: resonanceSummary?.totalInteractions || 0,
    bookmarkCount: bookmarks.length,
    verseClicks: history.filter(h => h.resonance_type === 'verse_click').length,
    moduleVisitCount: Object.values(moduleVisits).reduce((a, b) => a + b, 0),
    uniqueModules: Object.keys(moduleVisits).filter(k => moduleVisits[k] > 0).length,
    topNames: resonanceSummary?.topNames || [],
    topKeys: resonanceSummary?.topKeys || [],
    questsCompleted,
    streakDays: 0,
  };

  if (history.length > 0) {
    const dates = [...new Set(history.map(h => new Date(h.created_at).toDateString()))];
    let streak = 1;
    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i - 1]);
      const curr = new Date(dates[i]);
      const diff = (prev.getTime() - curr.getTime()) / (1000 * 60 * 60 * 24);
      if (diff <= 1.5) streak++;
      else break;
    }
    badgeStats.streakDays = streak;
  }

  const alhamdulilahCount = computeAlhamdulilahCount(badgeStats);
  const currentLevel = getAlhamdulilahLevel(alhamdulilahCount);
  const nextLevel = ALHAMDULILAH_LEVELS.find(l => l.level === currentLevel.level + 1);
  const countInLevel = alhamdulilahCount - currentLevel.minCount;
  const countNeeded = nextLevel ? nextLevel.minCount - currentLevel.minCount : 1;
  const levelProgress = Math.min(100, Math.round((countInLevel / countNeeded) * 100));

  const gardenStage = getGardenStage(alhamdulilahCount);
  const promptDensity = getPromptDensity(gardenStage);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedCycleAlhamdulilah = parseInt(localStorage.getItem('haal_cycle_alhamdulilah') || '0', 10);
    const storedCycleCount = parseInt(localStorage.getItem('haal_cycle_count') || '0', 10);
    if (alhamdulilahCount > 0 && alhamdulilahCount % CYCLE_CEILING === 0 && alhamdulilahCount !== storedCycleAlhamdulilah) {
      const newCycleCount = storedCycleCount + 1;
      localStorage.setItem('haal_cycle_count', String(newCycleCount));
      localStorage.setItem('haal_cycle_alhamdulilah', String(alhamdulilahCount));
      localStorage.setItem('haal_total_alhamdulilah', String(alhamdulilahCount));
      setCycleCount(newCycleCount);
      setHarvestSummary({
        topModules: Object.entries(moduleVisits).sort(([,a],[,b]) => b-a).slice(0,3).map(([k]) => k),
        topNames: badgeStats.topNames.slice(0, 3),
        strongestObservation: history.find(h => h.resonance_type === 'deep_read')?.content_title,
        cycleNumber: newCycleCount,
        totalCumulative: alhamdulilahCount,
      });
      setHarvestModalVisible(true);
      fireFounderVoice('first_alhamdulilah_harvest');
    }
  }, [alhamdulilahCount]);

  React.useEffect(() => {
    const recognitionTypes = ['verse_click', 'bookmark', 'deep_read', 'term_expand'];
    const actionTypes = ['module_visit', 'role_select'];
    const recognitionCount = history.filter(h => recognitionTypes.includes(h.resonance_type)).length;
    const actionCount = history.filter(h => actionTypes.includes(h.resonance_type)).length + (questsCompleted * 3);
    const computed = computeMizanState(recognitionCount, actionCount);
    setMizanData(computed);
    if (computed.state !== 'balanced' && history.length > 5) {
      fireFounderVoice('first_mizan_imbalance');
      fireFounderVoice('first_mizan_imbalance_garden');
    }
  }, [history, questsCompleted]);

  React.useEffect(() => {
    if (history.length === 1) fireFounderVoice('first_observation_garden');
  }, [history.length]);

  React.useEffect(() => {
    if (questsCompleted === 1) fireFounderVoice('first_amanah_quest');
  }, [questsCompleted]);

  // Barzakh transition on stage change
  const prevStageRef = useRef<GardenStage>(gardenStage);
  React.useEffect(() => {
    if (prevStageRef.current !== gardenStage) {
      const stageLabels: Record<GardenStage, string> = { aware: 'Aware', conscious: 'Conscious', acting: 'Acting' };
      setBarzakhFrom(stageLabels[prevStageRef.current]);
      setBarzakhTo(stageLabels[gardenStage]);
      setBarzakhType('stage_transition');
      setShowBarzakhTransition(true);
      prevStageRef.current = gardenStage;
    }
  }, [gardenStage]);

  const displayCycleCount = alhamdulilahCount % CYCLE_CEILING;
  const totalCycles = Math.floor(alhamdulilahCount / CYCLE_CEILING);
  const totalCumulative = typeof window !== 'undefined' ? parseInt(localStorage.getItem('haal_total_alhamdulilah') || '0', 10) : 0;

  const earnedBadges = BADGES.filter(b => b.condition(badgeStats));

  const TABS: { id: DashboardTab; label: string; icon: string }[] = [
    { id: 'garden', label: 'My Garden', icon: '⬡' },
    { id: 'the-mural', label: 'The Mural', icon: '◈' },
    { id: 'badges', label: 'Alhamdulilah', icon: '✦' },
    { id: 'history', label: 'History', icon: '◎' },
    { id: 'saved', label: 'Saved', icon: '◈' },
    { id: 'quests', label: 'Quests', icon: '⌘' },
    { id: 'garment', label: 'Garment', icon: '⬡' },
    { id: 'roadmap', label: 'Roadmap', icon: '◈' },
  ];

  const roleLabel = role.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const lineageLabel = lineage ? lineage.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;
  const userCity = typeof window !== 'undefined' ? (localStorage.getItem('haal_city') || 'chicago') : 'chicago';

  return (
    <div style={{ background: '#060d12', minHeight: '100vh', overflowX: 'hidden', position: 'relative' }}>
      {showFirstVisit && <FirstVisitReveal onComplete={() => setShowFirstVisit(false)} />}
      <AlFatihaSesssionOpening onEnter={() => {}} />
      <TafakkurMode active={tafakkurActive} onExit={() => setTafakkurActive(false)} prayerState={prayerState} />
      <MaghribAyahOverlay prayerState={prayerState} gardenVisitCount={gardenVisitCount} />
      <LineageEmergence interactionLog={interactionLog} onLineageSet={(l) => setLineage(l)} currentLineage={lineage} />
      <RootConnectionToast connection={rootConnection} onDismiss={() => setRootConnection(null)} />
      <DhikrNotification dhikrMode={dhikrMode} trigger={dhikrTrigger} onDismiss={() => setDhikrTrigger(null)} />
      <FajrDiscoveryToast />
      <HarvestModal
        visible={harvestModalVisible}
        summary={harvestSummary}
        onBeginNextCycle={() => {
          setHarvestModalVisible(false);
          if (typeof window !== 'undefined') localStorage.setItem('haal_cycle_alhamdulilah', String(alhamdulilahCount));
        }}
      />
      <PrayerOrientationTooltip prayerState={prayerState} onDismiss={() => {}} />
      <FounderVoice voiceKey={activeVoice} onDismiss={dismissFounderVoice} />
      <GardenExitOverlay />
      {niyyahContext && (
        <NiyyahModal
          open={niyyahModalOpen}
          actionType={niyyahContext.actionType}
          actionId={niyyahContext.actionId}
          actionLabel={niyyahContext.actionLabel}
          onConfirm={(record) => { setNiyyahModalOpen(false); niyyahContext.onConfirm(record); setNiyyahContext(null); }}
          onSkip={() => { setNiyyahModalOpen(false); setNiyyahContext(null); }}
        />
      )}
      <MuhasabaMoment
        open={muhasabaOpen}
        niyyahRecord={muhasabaRecord}
        onFulfilled={() => { setMuhasabaOpen(false); setMuhasabaRecord(null); }}
        onRevealed={() => { setMuhasabaOpen(false); setMuhasabaRecord(null); }}
      />
      {showBarzakhTransition && (
        <BarzakhTransition
          type={barzakhType}
          fromLabel={barzakhFrom}
          toLabel={barzakhTo}
          onReveal={() => setShowBarzakhTransition(false)}
        />
      )}
      <AtmosphericBackground prayerState={prayerState} soilPurity={soilPurity} />
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '100vh', pointerEvents: 'none', zIndex: 0, background: getHaalStateBackground(currentLevel, levelProgress), transition: 'background 2s ease' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header />
        <Breadcrumb items={[{ label: 'Dashboard', href: '/homepage' }, { label: 'My Garden' }]} />
        <main style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(80px, 15vw, 96px) clamp(12px, 4vw, 24px) clamp(48px, 8vw, 64px)' }}>
          {/* Page Header */}
          <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="font-mono text-xs tracking-widest uppercase mb-2" style={{ color: 'rgba(201,197,169,0.5)' }}>⬡ Caretaker Space</p>
              <h1 style={{ fontFamily: 'monospace', fontSize: 'clamp(20px, 5vw, 30px)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.02em', color: '#F5F0E8' }}>My Garden</h1>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 'clamp(11px, 2.5vw, 13px)', marginTop: 4 }}>
                Your personal plot — cultivate your mata, plant your tools, tend your path.
              </p>
              {lineageLabel && (
                <>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${currentLevel.color}80`, direction: 'rtl' }}>{currentLevel.arabic}</p>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>
                    {currentLevel.meaning}
                  </p>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                onClick={() => { const next = !dhikrMode; setDhikrMode(next); if (typeof window !== 'undefined') localStorage.setItem('haal_dhikr_mode', String(next)); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '7px 12px', borderRadius: 6, background: dhikrMode ? 'rgba(201,168,76,0.08)' : 'rgba(255,255,255,0.02)', border: `1px solid ${dhikrMode ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.08)'}`, color: dhikrMode ? '#c9a84c' : 'rgba(212,197,169,0.3)', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                <span style={{ fontSize: 14 }}>ذكر</span>
                {dhikrMode ? 'Dhikr ON' : 'Dhikr OFF'}
              </button>
              <TafakkurToggle onActivate={() => setTafakkurActive(true)} />
              <button
                onClick={() => exportJourneyArchive({ role, roleLabel, alhamdulilahCount, currentLevel, earnedBadges, bookmarks, history, quests, moduleVisits })}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, color: '#c9a84c', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'monospace', flexShrink: 0 }}
              >
                <span style={{ fontSize: 14 }}>⬇</span>
                Export Journey Archive
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="lg:w-60 flex-shrink-0">
              <div className="rounded-xl p-5 mb-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                {/* Avatar + Level */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-mono text-lg font-black" style={{ background: `${currentLevel.color}15`, border: `2px solid ${currentLevel.color}50`, color: currentLevel.color }}>
                      {roleLabel.charAt(0)}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center font-mono text-[8px] font-black" style={{ background: currentLevel.color, color: '#060d12' }}>
                      {currentLevel.level}
                    </div>
                  </div>
                  <div>
                    <p className="font-mono text-xs font-bold uppercase tracking-wide" style={{ color: '#F5F0E8' }}>Caretaker</p>
                    <div className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded mt-1" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c', display: 'inline-block' }}>
                      {roleLabel}
                    </div>
                  </div>
                </div>

                {/* Alhamdulilah progress */}
                <div className="mb-4 p-3 rounded-lg" style={{ background: `${currentLevel.color}08`, border: `1px solid ${currentLevel.color}20` }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: currentLevel.color }}>{currentLevel.label}</span>
                    <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.4)' }}>الحمد لله</span>
                  </div>
                  <div className="h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${levelProgress}%`, background: currentLevel.color }} />
                  </div>
                  {nextLevel && <p className="font-mono text-[8px] mt-1" style={{ color: 'rgba(212,197,169,0.3)' }}>{countNeeded - countInLevel} more toward {nextLevel.label}</p>}
                </div>

                <ThreeStageProgressBar alhamdulilahCount={alhamdulilahCount} />

                {/* Mizan Indicator */}
                <div className="mb-4">
                  <MizanIndicator
                    mizan={mizanData}
                    mostRecentEncounter={history.find(h => h.resonance_type === 'deep_read')?.content_title}
                    resourcesVerifiedThisWeek={quests.filter(q => q.status === 'completed').length}
                  />
                </div>

                {/* Sunnah Module toggle */}
                <button
                  onClick={() => setShowSunnahModule(v => !v)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: showSunnahModule ? 'rgba(201,168,76,0.08)' : 'rgba(255,255,255,0.02)', border: `1px solid ${showSunnahModule ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.05)'}`, borderRadius: 8, cursor: 'pointer', marginBottom: 12, transition: 'all 0.2s' }}
                >
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#c9a84c' }}>الهادي</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: showSunnahModule ? '#c9a84c' : 'rgba(212,197,169,0.35)' }}>Sunnah Module</span>
                  <span style={{ marginLeft: 'auto', color: 'rgba(201,197,169,0.3)', fontSize: 10 }}>{showSunnahModule ? '▲' : '▼'}</span>
                </button>

                {/* Alhamdulilah count */}
                <div className="flex items-center justify-between mb-4 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>الحمد لله</span>
                  <div style={{ textAlign: 'right' }}>
                    <span className="font-mono text-sm font-black" style={{ color: '#c9a84c' }}>{alhamdulilahCount}</span>
                    <p className="font-mono text-[7px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.25)' }}>recognitions</p>
                  </div>
                </div>

                {/* Badge count */}
                <div className="flex items-center justify-between mb-4 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Alhamdulillah</span>
                  <span className="font-mono text-sm font-black" style={{ color: '#c9a84c' }}>{earnedBadges.length}/{BADGES.length}</span>
                </div>

                {!user && (
                  <div className="rounded p-3 mb-4" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
                    <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, marginBottom: 8 }}>Your journey is being saved locally.</p>
                    <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, marginTop: 4 }}>Create an account to sync across devices.</p>
                  </div>
                )}

                {/* Nav */}
                <nav className="space-y-1">
                  {TABS.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className="w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded transition-all duration-200 font-mono text-[10px] uppercase tracking-widest"
                      style={{ background: activeTab === tab.id ? 'rgba(201,168,76,0.08)' : 'transparent', color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.4)', borderLeft: activeTab === tab.id ? '2px solid #c9a84c' : '2px solid transparent' }}
                    >
                      <span style={{ fontSize: 12 }}>{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Stats */}
              {resonanceSummary && (
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Quick Stats</p>
                  {[
                    { label: 'Total Recognitions', value: resonanceSummary.totalInteractions },
                    { label: 'Bookmarks', value: bookmarks.length },
                    { label: 'History Items', value: history.length },
                    { label: 'Quests Done', value: questsCompleted },
                  ].map(stat => (
                    <div key={stat.label} className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                      <span style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11 }}>{stat.label}</span>
                      <span className="font-mono text-sm font-black" style={{ color: '#c9a84c' }}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Sunnah Module Panel */}
              {showSunnahModule && (
                <div className="mt-4">
                  <SunnahModule currentStage={gardenStage === 'aware' ? 'aware' : gardenStage === 'conscious' ? 'conscious' : 'acting'} />
                </div>
              )}

              {/* Role Selector with Lineage */}
              <div className="mb-4">
                <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.3)' }}>Vocational Lens</p>
                <RoleSelectorWithLineage
                  currentRole={vocationLens}
                  currentLineage={lineage}
                  onRoleChange={(newRole) => {
                    setVocationLens(newRole);
                    if (typeof window !== 'undefined') localStorage.setItem('haal_vocation_lens', newRole);
                  }}
                />
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="space-y-4">
                  <GardenLoadingVoice prayerState={prayerState} />
                  <Skeleton height={200} />
                  <Skeleton height={120} />
                  <Skeleton height={80} />
                </div>
              ) : (
                <>
                  {activeTab === 'garden' && (
                    <DashboardErrorBoundary fallbackLabel="Garden" fallbackIcon="⬡">
                      <GardenTabContent
                        role={role}
                        roleLabel={roleLabel}
                        badgeStats={badgeStats}
                        earnedBadges={earnedBadges}
                        alhamdulilahCount={alhamdulilahCount}
                        currentLevel={currentLevel}
                        nextLevel={nextLevel}
                        levelProgress={levelProgress}
                        quests={quests}
                        bookmarks={bookmarks}
                        history={history}
                        moduleVisits={moduleVisits}
                        prayerState={prayerState}
                        gardenVisitCount={gardenVisitCount}
                        gardenStage={gardenStage}
                        displayCycleCount={displayCycleCount}
                        totalCycles={totalCycles}
                        totalCumulative={totalCumulative}
                        userCity={userCity}
                        resonanceSummary={resonanceSummary}
                        vocationLens={vocationLens}
                        onWidgetInteract={(widgetId) => {
                          setInteractionLog(prev => [...prev, widgetId].slice(-10));
                        }}
                      />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'the-mural' && (
                    <DashboardErrorBoundary fallbackLabel="The Mural" fallbackIcon="◈">
                      <FrameworkMapTabContent resonanceSummary={resonanceSummary} moduleVisits={moduleVisits} history={history} />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'badges' && (
                    <DashboardErrorBoundary fallbackLabel="Badges" fallbackIcon="✦">
                      <BadgesTabContent earnedBadges={earnedBadges} allBadges={BADGES} badgeStats={badgeStats} alhamdulilahCount={alhamdulilahCount} currentLevel={currentLevel} nextLevel={nextLevel} levelProgress={levelProgress} countNeeded={countNeeded} countInLevel={countInLevel} />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'history' && (
                    <DashboardErrorBoundary fallbackLabel="History" fallbackIcon="◎">
                      <HistoryTabContent history={history} />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'saved' && (
                    <DashboardErrorBoundary fallbackLabel="Saved" fallbackIcon="◈">
                      <SavedTabContent bookmarks={bookmarks} user={user} />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'quests' && (
                    <DashboardErrorBoundary fallbackLabel="Quests" fallbackIcon="⌘">
                      <QuestsTabContent quests={quests} onComplete={loadData} role={role} resonanceSummary={resonanceSummary} history={history} bookmarks={bookmarks} moduleVisits={moduleVisits} />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'garment' && (
                    <DashboardErrorBoundary fallbackLabel="Garment" fallbackIcon="⬡">
                      <GarmentTabContent resonanceSummary={resonanceSummary} bookmarks={bookmarks} history={history} role={role} roleLabel={roleLabel} />
                    </DashboardErrorBoundary>
                  )}
                  {activeTab === 'roadmap' && (
                    <DashboardErrorBoundary fallbackLabel="Roadmap" fallbackIcon="◈">
                      <RoadmapTabContent role={role} roleLabel={roleLabel} alhamdulilahCount={alhamdulilahCount} currentLevel={currentLevel} earnedBadges={earnedBadges} quests={quests} />
                    </DashboardErrorBoundary>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}

// ─── ERROR BOUNDARY ───────────────────────────────────────────────────────────

interface ErrorBoundaryState { hasError: boolean; error?: Error; }
interface ErrorBoundaryProps { children: ReactNode; fallbackLabel?: string; fallbackIcon?: string; }

class DashboardErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState { return { hasError: true, error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.warn('[Dashboard Error Boundary]', error.message); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ background: 'rgba(180,80,80,0.06)', border: '1px solid rgba(180,80,80,0.2)', borderRadius: 8, padding: '20px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, marginBottom: 8, opacity: 0.4 }}>{this.props.fallbackIcon || '◎'}</div>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, fontFamily: 'monospace', marginBottom: 6 }}>{this.props.fallbackLabel || 'This section'} encountered an issue</p>
          <button onClick={() => this.setState({ hasError: false, error: undefined })} style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', padding: '5px 14px', borderRadius: 4, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(212,197,169,0.2)', color: 'rgba(212,197,169,0.5)' }}>Try Again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── GARDEN TAB ───────────────────────────────────────────────────────────────

function GardenTabContent({
  role, roleLabel, badgeStats, earnedBadges, alhamdulilahCount, currentLevel, nextLevel,
  levelProgress, quests, bookmarks, history, moduleVisits, prayerState, gardenVisitCount,
  gardenStage, displayCycleCount, totalCycles, totalCumulative, userCity, resonanceSummary,
  vocationLens,
  onWidgetInteract,
}: {
  role: string; roleLabel: string; badgeStats: BadgeStats; earnedBadges: Badge[];
  alhamdulilahCount: number; currentLevel: AlhamdulilahLevel; nextLevel: AlhamdulilahLevel | undefined;
  levelProgress: number; quests: Quest[]; bookmarks: BookmarkItem[]; history: HistoryItem[];
  moduleVisits: Record<string, number>; prayerState: PrayerState; gardenVisitCount: number;
  gardenStage: GardenStage; displayCycleCount: number; totalCycles: number; totalCumulative: number;
  userCity: string; resonanceSummary: ResonanceSummary | null;
  vocationLens: string;
  onWidgetInteract: (widgetId: string) => void;
}) {
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('haal_active_widgets');
      if (saved) return JSON.parse(saved);
    }
    return getRoleAdaptiveWidgets(role, moduleVisits);
  });
  const [notes, setNotes] = useState('');
  const [gardenView, setGardenView] = useState<'canvas' | 'depth'>('canvas');
  const threadCount = Math.min(40, Math.max(6, bookmarks.length + history.length));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedNotes = localStorage.getItem('haal_caretaker_notes') || '';
      setNotes(savedNotes);
    }
  }, []);

  return (
    <div className="space-y-6">
      <LongleafPineNote garmentThreadCount={threadCount} />

      {/* Garden view toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, overflow: 'hidden' }}>
          <button
            onClick={() => setGardenView('canvas')}
            style={{ padding: '8px 16px', background: gardenView === 'canvas' ? 'rgba(201,168,76,0.12)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,0.07)', color: gardenView === 'canvas' ? '#c9a84c' : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            ⬡ Kitbash Canvas
          </button>
          <button
            onClick={() => setGardenView('depth')}
            style={{ padding: '8px 16px', background: gardenView === 'depth' ? 'rgba(201,168,76,0.12)' : 'transparent', border: 'none', color: gardenView === 'depth' ? '#c9a84c' : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            ◎ Depth Layers
          </button>
        </div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>
          {gardenView === 'canvas' ? 'Drag · Resize · Plant · Embed' : 'Yaqeen · Cycles · Roots · Soil'}
        </p>
      </div>

      {/* ── KITBASH CANVAS VIEW ── */}
      {gardenView === 'canvas' && (
        <KitbashGardenCanvas />
      )}

      {/* ── CLAUDE BATCH PROCESSOR — sequential photo analysis, observation chains ── */}
      {gardenView === 'canvas' && (
        <ClaudeBatchProcessor />
      )}

      {/* ── DEPTH LAYERS VIEW ── */}
      {gardenView === 'depth' && (
        <div className="space-y-6">
          <OpenLetterQuoteBar />
          <DualVoiceDisplay
            messageKey={prayerState === 'fajr' ? 'garden_greeting_fajr' : gardenVisitCount > 3 ? 'garden_greeting_return' : 'garden_greeting_morning'}
            defaultRegister="companion"
            compact={false}
          />
          <NiyyahField />
          <CommunityGardenContext>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0 8px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.08)', borderRadius: 12, marginBottom: 8 }}>
              <FitrahSeed />
            </div>
          </CommunityGardenContext>

          {/* Alhamdulilah banner */}
          <div className="rounded-xl p-5" style={{ background: `${currentLevel.color}08`, border: `1px solid ${currentLevel.color}20`, borderLeft: `3px solid ${currentLevel.color}` }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: `${currentLevel.color}80` }}>
                  الحمد لله — {displayCycleCount} of {CYCLE_CEILING} this cycle
                </p>
                <h3 className="font-mono text-lg font-black uppercase" style={{ color: '#F5F0E8' }}>{currentLevel.label}</h3>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${currentLevel.color}80`, direction: 'rtl' }}>{currentLevel.arabic}</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>{currentLevel.meaning}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-2xl font-black" style={{ color: currentLevel.color }}>{displayCycleCount}</p>
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(201,197,169,0.3)' }}>this cycle</p>
                {totalCycles > 0 && <p className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.25)', marginTop: 2 }}>Cycles complete: {totalCycles}</p>}
              </div>
            </div>
            <div className="mt-3">
              <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, (displayCycleCount / CYCLE_CEILING) * 100)}%`, background: `linear-gradient(90deg, ${currentLevel.color}80, ${currentLevel.color})` }} />
              </div>
            </div>
          </div>

          {/* Plot 3 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ padding: '16px 0 4px', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 4 }}>✦ Plot 3 — Roots Ma'rifah Enhancements</div>
            </div>
            <YaqeenWeightedCounter />
            <TripartiteAyatSystem />
            <div>
              <AynYaqeenTensionNote context="Quran Encounter Module" />
              <div style={{ marginTop: 10 }}><QuranEncounterModule /></div>
            </div>
            <InnerHawasModule />
            <ImperfectionModel />
            <CommunityRoleModuleCreator />
          </div>

          {/* Plot 4 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ padding: '16px 0 4px', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 4 }}>✦ Plot 4 — Role Paths · Yaqeen Tiles · Subsurface Layers</div>
            </div>
            <AlhamdulillahFoundationalCounter count={alhamdulilahCount} role={roleLabel} />
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>◈ Lineage Dashboard</p>
              <LineageDashboards />
            </div>
            <RoleGardenDashboard />
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>Module Yaqeen Tracker</p>
              <div className="space-y-2">
                <YaqeenModuleTile moduleId="irl-lens" moduleLabel="IRL Lens" moduleColor="#2d8a87" href="/inner-reality-lens" />
                <YaqeenModuleTile moduleId="keys" moduleLabel="Keys to Kulli Haal" moduleColor="#D4A82A" href="/keys-to-kulli-haal" />
                <YaqeenModuleTile moduleId="mrg" moduleLabel="MRG" moduleColor="#2d7a47" href="/muslim-resource-guide" />
                <YaqeenModuleTile moduleId="names" moduleLabel="Names" moduleColor="#8a8ab8" href="/names-reference" />
              </div>
            </div>
            <SubsurfaceLayersVisualization />
            <RemindIfBenefitsCheck sessionCount={gardenVisitCount} />
          </div>

          {/* Plot 5 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ padding: '16px 0 4px', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 4 }}>✦ Plot 5 — Vocation Lens · Root Modules · Revelation Language</div>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>Module Tiles — {roleLabel} Lens Active</p>
              <div className="space-y-2">
                <ModuleTileWithRolePrompt moduleId="irl-lens" moduleLabel="IRL Lens" moduleColor="#2d8a87" href="/inner-reality-lens" activeRole={vocationLens} />
                <ModuleTileWithRolePrompt moduleId="keys" moduleLabel="Keys to Kulli Haal" moduleColor="#D4A82A" href="/keys-to-kulli-haal" activeRole={vocationLens} />
                <ModuleTileWithRolePrompt moduleId="mrg" moduleLabel="MRG" moduleColor="#2d7a47" href="/muslim-resource-guide" activeRole={vocationLens} />
                <ModuleTileWithRolePrompt moduleId="names" moduleLabel="Names" moduleColor="#8a8ab8" href="/names-reference" activeRole={vocationLens} />
                <ModuleTileWithRolePrompt moduleId="ayat" moduleLabel="Ayat" moduleColor="#2d7a47" href="/ayat-in-creation" activeRole={vocationLens} />
              </div>
            </div>
            <VocationLensModule />
            <VocationalConnectionsMap />
            <RootModulesArchitecture />
            <IRLLensSelfReferential />
          </div>

          <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.3)' }}>Ward Revisitation Depth</p>
            <RecursiveRevisitationSystem wardName="Your Ward" biome="Forest" />
          </div>

          <FikrJournalModule />
          <ActingStageFooter gardenStage={gardenStage} />
        </div>
      )}
    </div>
  );
}

// ─── FRAMEWORK MAP TAB (formerly The Mural) ───────────────────────────────────